/* eslint-disable no-type-assertion/no-type-assertion -- adapter sits between f0's RenderableTurn types and CopilotKit's looser Message shapes. */

import { type ReactNode, useCallback, useMemo } from 'react'

import {
  useCopilotChatInternal as useCopilotChat,
  useCopilotContext,
  useLazyToolRenderer,
} from '@copilotkit/react-core'
import { Markdown } from '@copilotkit/react-ui'

import {
  type F0AIMessage,
  F0AiMessagesContainer,
  type F0AiMessagesContainerProps,
  type F0Message,
  type RenderableTurn,
  useAiChat as useF0AiChat,
} from '@factorialco/f0-react'

import { useClarifyingQuestion } from 'modules/ai/chat/providers/ClarifyingQuestionProvider'
import { useFactorialChatRuntime } from 'modules/ai/chat/providers/FactorialChatRuntimeProvider'
import { useOrderedMessageParts } from 'modules/ai/chat/providers/OrderedMessagePartsProvider'
import { copilotMessagesToF0 } from 'modules/ai/chat/protocol/messageAdapter'
import {
  analyzeTurn,
  convertMessagesToTurns,
  expandFromOrderedParts,
  extractThinkingGroup,
  filterNonRenderableMessages,
  legacyExpansion,
} from 'modules/ai/chat/protocol/turnUtils'

function parseThinkingTitle(argsJson: string): string {
  try {
    const parsed = JSON.parse(argsJson)
    return (parsed.message as string) || 'thinking'
  } catch {
    return 'thinking'
  }
}

function collectThinkingTitles(messages: F0Message[]): string[] {
  const titles: string[] = []
  for (const message of messages) {
    if (message.role !== 'assistant') continue
    const toolCalls = message.toolCalls
    for (const tc of toolCalls ?? []) {
      if (tc.function.name === 'orchestratorThinking') {
        titles.push(parseThinkingTitle(tc.function.arguments))
      }
    }
  }
  return titles
}

const RESPONSE_STOPPED_MARKER = '<!--response-stopped-->'

export const ConnectedMessagesContainer = ({
  noShadows = false,
  children,
}: {
  noShadows?: boolean
  children?: ReactNode
} = {}) => {
  const { messages: rawCkMessages, interrupt, isLoading } = useCopilotChat()
  const { getOrderedParts, version: orderedPartsVersion } = useOrderedMessageParts()
  const { threadId } = useCopilotContext()
  const lazyToolRendered = useLazyToolRenderer()
  const { isLoadingThread } = useFactorialChatRuntime()

  const {
    initialMessage,
    visualizationMode,
    tracking,
    setPendingQuote,
    openGame,
    onThumbsUp,
    onThumbsDown,
  } = useF0AiChat()
  const { clarifyingQuestion } = useClarifyingQuestion()

  const inProgress = isLoading
  const ckMessages = useMemo(() => copilotMessagesToF0(rawCkMessages), [rawCkMessages])

  // CopilotKit packs multiple tool calls + text into one assistant message;
  // split each back into one sub-message per segment so the UI can render
  // text and tool cards in the order they streamed in.
  const filteredMessages = useMemo(
    () =>
      filterNonRenderableMessages(ckMessages).flatMap((msg) => {
        if (msg.role !== 'assistant') return [msg]
        const ordered = getOrderedParts(msg.id)
        if (ordered && ordered.length > 0) {
          return expandFromOrderedParts(msg, ordered)
        }
        return legacyExpansion(msg)
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ckMessages, getOrderedParts, orderedPartsVersion]
  )

  const renderToolCall = useCallback<NonNullable<F0AiMessagesContainerProps['renderToolCall']>>(
    (message): ReactNode => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const renderer = lazyToolRendered(message as any, filteredMessages as any)
      return renderer?.() ?? null
    },
    [lazyToolRendered, filteredMessages]
  )

  const turns = useMemo<RenderableTurn[]>(() => {
    const rawTurns = convertMessagesToTurns(filteredMessages)
    return rawTurns.map((turnMessages, turnIndex) => {
      const { turnIsComplete, showActivityIndicator } = analyzeTurn(
        turnMessages,
        turnIndex,
        rawTurns.length,
        inProgress
      )
      const isLastTurn = turnIndex === rawTurns.length - 1
      const { thinkingGroup, restMessages } = extractThinkingGroup(turnMessages)

      const thinking =
        thinkingGroup && thinkingGroup.length > 0
          ? {
              titles: collectThinkingTitles(thinkingGroup),
              inProgress: !turnIsComplete,
              isWriting: showActivityIndicator,
            }
          : undefined

      const flatRest = restMessages.filter((m): m is F0Message => !Array.isArray(m))
      const userMessages = flatRest.filter((m) => m.role === 'user')
      const assistantMessages = flatRest.filter((m) => m.role !== 'user')

      const assistantContent = assistantMessages
        .filter((m) => typeof m.content === 'string' && (m.content as string).length > 0)
        .map((m) => m.content as string)
        .join('\n\n')
      const isStopped = assistantContent.includes(RESPONSE_STOPPED_MARKER)

      const lastAssistantMessage = [...assistantMessages]
        .reverse()
        .find((m): m is F0AIMessage => m.role === 'assistant')

      const feedbackEntry =
        turnIsComplete && assistantContent && lastAssistantMessage && !isStopped
          ? {
              content: assistantContent,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              targetMessage: lastAssistantMessage as any,
            }
          : undefined

      const lastTurnMessage = turnMessages[turnMessages.length - 1]
      const isLastTurnLastMessageUser =
        !Array.isArray(lastTurnMessage) && lastTurnMessage?.role === 'user'
      const endIndicator: RenderableTurn['endIndicator'] =
        inProgress && isLastTurn && isLastTurnLastMessageUser
          ? 'thinking'
          : showActivityIndicator
            ? 'activity'
            : undefined

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        userMessages: userMessages as any,
        thinking,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        assistantMessages: assistantMessages as any,
        isInProgress: !turnIsComplete,
        endIndicator,
        feedback: feedbackEntry,
      }
    })
  }, [filteredMessages, inProgress])

  const onWelcomeClick = useCallback(() => {
    openGame('pong')
  }, [openGame])

  const onReplyQuote = useCallback(
    (text: string) => {
      setPendingQuote({ text })
    },
    [setPendingQuote]
  )

  const onAssistantMessageRendered = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (message: any) => {
      tracking?.onMessage?.(message)
    },
    [tracking]
  )

  const feedback = useMemo<F0AiMessagesContainerProps['feedback']>(() => {
    if (!onThumbsUp && !onThumbsDown) return undefined
    return {
      threadId,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onThumbsUp: (onThumbsUp ?? (() => {})) as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onThumbsDown: (onThumbsDown ?? (() => {})) as any,
    }
  }, [threadId, onThumbsUp, onThumbsDown])

  const renderMarkdown = useCallback<NonNullable<F0AiMessagesContainerProps['renderMarkdown']>>(
    (content) => <Markdown content={content} />,
    []
  )

  return (
    <F0AiMessagesContainer
      turns={turns}
      isLoadingThread={isLoadingThread}
      interrupt={interrupt}
      initialMessage={initialMessage}
      onWelcomeClick={onWelcomeClick}
      renderToolCall={renderToolCall}
      onReplyQuote={onReplyQuote}
      onAssistantMessageRendered={onAssistantMessageRendered}
      autoScrollUserIntoView={visualizationMode !== 'fullscreen'}
      renderMarkdown={renderMarkdown}
      feedback={feedback}
      freezeLayout={clarifyingQuestion != null}
      noShadows={noShadows}
    >
      {children}
    </F0AiMessagesContainer>
  )
}
